import { Flex, OverflowText, Icon, Text, isNullish } from "@theorgchartdev/storybook";
import cx from "classnames";
import { appstr } from "@coreapp/utils/appstr";
import { getImagePath } from "@coreapp/utils/getImagePath";
import { AssignmentStatusPill } from "../AssignmentStatusPill";
import { SanitizeFieldValue, IsFieldNotConfigured } from "../../RecordInfoCard/utils";
import { getFieldValuePair } from "@/Utils/DataLib";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import { EmployeeFieldConfig } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/ProfileConfiguration/ProfileConfiguration.utils";
import { FavoriteManagerInterface } from "../EmployeeBaseballCard";

type Props = {
  config: EmployeeFieldConfig;
  header1Text: string;
  showHeader1?: boolean;

  subheader1Text: string;
  showSubheader1?: boolean;

  header2Text: string;
  showHeader2?: boolean;

  header3Text?: string;
  showHeader3?: boolean;

  photoSrc?: string | null;
  showAssignmentStatus?: boolean;

  favoriteController?: FavoriteManagerInterface;

  ignoreFieldDefCheck?: boolean;

  record: Record<string, string>;
};

const DEFAULT_PHOTO_SRC = "/static/images/OC-logo.png";

export const EmployeeHeadingProfileCard = (props: Props) => {
  const {
    config,
    header1Text,
    showHeader1 = true,
    subheader1Text,
    showSubheader1 = true,
    header2Text,
    showHeader2 = true,
    header3Text,
    showHeader3 = true,

    photoSrc,
    showAssignmentStatus = false,

    record,
    favoriteController,
    ignoreFieldDefCheck = false
  } = props;

  const baseClass = "oc-profile-heading";
  const placeHolder = appstr("Blank");
  const header1NotConfigured = IsFieldNotConfigured(header1Text, null);
  const subheader1NotConfigured = IsFieldNotConfigured(subheader1Text, null);
  const header2NotConfigured = IsFieldNotConfigured(header2Text, null);
  const header3NotConfigured = IsFieldNotConfigured(header3Text, null);
  const phoneNotConfigured = IsFieldNotConfigured(record[config.phone], null);
  const locationNotConfigured = IsFieldNotConfigured(record[config.location], null);
  const emailNotConfigured = IsFieldNotConfigured(record[config.email], null);
  const enableStarring = favoriteController?.isStarringEnabled;
  const starredFields = favoriteController?.getCurrentFavorites();

  return (
    <Flex direction="column" gap="16px" className={baseClass}>
      <Flex direction="row" gap="12px">
        {config.showProfile && (
          <img
            className={cx("responsiveImage", `${baseClass}-photo`)}
            src={photoSrc ?? getImagePath(DEFAULT_PHOTO_SRC)}
            alt={header1Text ? `${header1Text} profile photo` : "Profile photo"}
          />
        )}
        <Flex direction="column" className={`${baseClass}-basic-fields`}>
          <Flex
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-end"
            gap="8px"
            className={`${baseClass}-basic-info`}
          >
            {showHeader1 && (
              <OverflowText
                clamp={2}
                size="large"
                dataTestId={`${baseClass}-header1`}
                className="oc-font-weight-500"
                colorModifier={header1NotConfigured ? "faintgrey" : undefined}
                modifier={header1NotConfigured ? "italic" : undefined}
              >
                {SanitizeFieldValue(header1Text, null, placeHolder)}
              </OverflowText>
            )}
            {showSubheader1 && (
              <OverflowText
                size="xsmall"
                colorModifier={subheader1NotConfigured ? "faintgrey" : "grey"}
                modifier={subheader1NotConfigured ? "italic" : undefined}
                dataTestId={`${baseClass}-subheader1`}
              >
                {SanitizeFieldValue(subheader1Text, null, placeHolder)}
              </OverflowText>
            )}
          </Flex>
          {showHeader2 && (
            <OverflowText
              modifier={header2NotConfigured ? "italic" : "bold"}
              colorModifier={header2NotConfigured ? "faintgrey" : undefined}
              size="medium"
              dataTestId={`${baseClass}-header2`}
            >
              {SanitizeFieldValue(header2Text, null, placeHolder)}
            </OverflowText>
          )}
          {showHeader3 && (
            <OverflowText
              size="small"
              dataTestId={`${baseClass}-header3`}
              colorModifier={header3NotConfigured ? "faintgrey" : undefined}
              modifier={header3NotConfigured ? "italic" : undefined}
            >
              {SanitizeFieldValue(header3Text, null, placeHolder)}
            </OverflowText>
          )}
        </Flex>
      </Flex>

      {showAssignmentStatus && (
        <AssignmentStatusPill
          record={record}
          labelProps={{ size: "small", className: "oc-font-weight-500" }}
          renderBlank
          withColon
        />
      )}

      <Flex direction="column" gap="4px" className={`${baseClass}-additional-info`}>
        {config.phone && (
          <Flex justifyContent="flex-start" gap="8px">
            <Icon name="phone" size="small" />
            <OverflowText
              size="small"
              colorModifier={phoneNotConfigured ? "faintgrey" : undefined}
              modifier={phoneNotConfigured ? "italic" : undefined}
            >
              {SanitizeFieldValue(record[config.phone], null, "--")}
            </OverflowText>
          </Flex>
        )}
        {config.location && (
          <Flex justifyContent="flex-start" gap="8px">
            <Icon name="location-dot" size="small" />
            <OverflowText
              size="small"
              colorModifier={locationNotConfigured ? "faintgrey" : undefined}
              modifier={locationNotConfigured ? "italic" : undefined}
            >
              {SanitizeFieldValue(record[config.location], null, "--")}
            </OverflowText>
          </Flex>
        )}
        {config.email && (
          <Flex justifyContent="flex-start" gap="8px">
            <Icon name="at" size="small" />
            <OverflowText
              size="small"
              colorModifier={emailNotConfigured ? "faintgrey" : undefined}
              modifier={emailNotConfigured ? "italic" : undefined}
            >
              {SanitizeFieldValue(record[config.email], null, "--")}
            </OverflowText>
          </Flex>
        )}
      </Flex>

      {!isNullish(starredFields) && starredFields.length > 0 && (
        <Flex dataTestId="profile-starred-fields" direction="column">
          {starredFields.map((field: string) => {
            const rawRecordValue = record[field];
            const fVPair = ignoreFieldDefCheck
              ? { value: rawRecordValue, field }
              : getFieldValuePair(field, rawRecordValue);
            const starredNotConfigured = IsFieldNotConfigured(
              rawRecordValue,
              "fieldDef" in fVPair ? fVPair.fieldDef : null
            );
            return (
              <ActionListItem
                key={field}
                buttonsInHierarchy={true}
                actions={
                  enableStarring
                    ? [
                        {
                          iconName: "star-solid",
                          iconProps: {
                            color: "brand"
                          },
                          onClick: () => favoriteController?.removeStarredField(fVPair),
                          dataTestId: `profile-remove-star-field--${fVPair.field}`
                        }
                      ]
                    : []
                }
                contentBefore={
                  <Flex justifyContent="space-between" width="100%">
                    <Text size="small" className="oc-font-weight-500" style={{ flex: 1 }}>
                      {fVPair.field || field}
                    </Text>
                    <Text
                      size="small"
                      style={{ flex: 1 }}
                      colorModifier={starredNotConfigured ? "faintgrey" : undefined}
                      modifier={starredNotConfigured ? "italic" : undefined}
                    >
                      {fVPair.value ?? "--"}
                    </Text>
                  </Flex>
                }
              />
            );
          })}
        </Flex>
      )}
    </Flex>
  );
};
